import { useHistory } from 'react-router-dom';
import { getDatasetControlDatasetActionTemplateData } from '../../../../api/utils/requestData/getDatasetControlDatasetActionTemplateData';
import {
  DatasetDataApiRequestData,
  SubsetDataApiRequestData,
} from '../../../../api/types/types';
import { DATASET_CONTROL_DATA_PARAM } from '../../../../constants';
import { DatasetControlAction } from '../../../../types/types';
import { resolveTemplate } from '../../../../utils/json/resolveTemplate';
import useRoutes from '../../../useRoutes';
import { useControlActionModalContent } from '../../../useControlActionModalContent';
import { displayConfirmationModal } from '../displayConfirmationModal';
import { getResolvedRowsToUse } from '../getResolvedRowsToUse';
import { debugLogger } from '../../../../debug/DebugLogger';

const log = debugLogger.scope('datasetControl');

// Opens the target dataset of a `dataset` control. The control's data
// template is resolved against the source grid's request data + selected row
// and written into the URL
export const getDatasetTypeCallback = (
  dataset: any,
  rowsToUse: any[],
  currentRow: any,
  selectedVirtualRows: number,
  deselectedRowIds: Set<string>,
  control: any,
  routes: ReturnType<typeof useRoutes>,
  history: ReturnType<typeof useHistory>,
  fromPathname: string,
  requestData: DatasetDataApiRequestData | SubsetDataApiRequestData,
  fetchDatasetData: () => Promise<any[]>,
  openControlModal: ReturnType<typeof useControlActionModalContent>['open'],
  closeControlModal: ReturnType<typeof useControlActionModalContent>['close'],
  setModalLoading: ReturnType<
    typeof useControlActionModalContent
  >['setModalLoading']
) => {
  return async () => {
    try {
      const action: DatasetControlAction = control.action;

      const shouldDisplayConfirmationModal = displayConfirmationModal(
        control,
        rowsToUse
      );

      if (shouldDisplayConfirmationModal) {
        setModalLoading(true);
      }

      const resolvedRowsToUse: any[] = await getResolvedRowsToUse(
        dataset,
        rowsToUse,
        selectedVirtualRows,
        fetchDatasetData,
        deselectedRowIds
      );

      const proceed = async () => {
        const templateData = getDatasetControlDatasetActionTemplateData(
          dataset,
          currentRow,
          resolvedRowsToUse,
          requestData
        );

        // resolveTemplate keeps the JSON type of whole-string placeholders,
        // so a numeric {{ id }} stays a number in the serialized data.
        const resolvedData = action.data
          ? await resolveTemplate(action.data, templateData)
          : undefined;

        /** @debugFeature datasetControl Logs a dataset control action building the ?data url param: the data template, resolved data and target dataset — undefined resolved data navigates with no param. */
        log('dataset control url param built', () => ({
          targetDatasetId: action.datasetId,
          dataTemplate: action.data,
          resolvedData,
        }));

        const search = resolvedData
          ? `?${DATASET_CONTROL_DATA_PARAM}=${encodeURIComponent(
              JSON.stringify(resolvedData)
            )}`
          : '';

        if (shouldDisplayConfirmationModal) {
          closeControlModal();
        }
        history.push({
          pathname: routes.datasetList(action.datasetId),
          search,
          state: { fromPathname },
        });
      };

      if (shouldDisplayConfirmationModal) {
        setModalLoading(false);
        return openControlModal(dataset, control, resolvedRowsToUse, proceed);
      }

      await proceed();
    } catch (error) {
      setModalLoading(false);
      throw error;
    }
  };
};
